import { action, computed, makeObservable, observable } from 'mobx';
import { ContentType, LessonMode, LessonSlideIds } from '../../Constants';

import multichoiceManager from './MultichoiceManager';
import responseManager from './ResponseManager';
import toolbarManager from './ToolbarManager';
import userManager from './UserManager';
import docReaderManager from './DocReaderManager';

import UtilsService from '../services/UtilsService';
import { getPageForActivityChild } from '../services/PdfActivityService';

import DefaultQuestion from '../../manuscript/questions/DefaultQuestion';
import Analogies from '../../manuscript/graphicorganizers/Analogies';
import CategoryTree from '../../manuscript/graphicorganizers/CategoryTree';
import DefaultGraphicOrganizer from '../../manuscript/graphicorganizers/DefaultGraphicOrganizer';
import DefaultInstruction from '../../manuscript/instructions/DefaultInstruction';
import DefaultResource from '../../manuscript/resources/DefaultResource';
import DefaultActivity from '../../manuscript/activities/DefaultActivity';
import Google from '../../manuscript/google/Google';

import Likert from '../components/lessonItems/Likert';
import TitlePage from '../components/TitlePage';
import SummaryPage from '../components/summaryPage/SummaryPage';
import ImageLabelMultichoice from '../components/lessonItems/ImageLabelMultichoice';
import ImageTextMatch from '../components/lessonItems/ImageTextMatch';
import ImageTextMatchFlip from '../components/lessonItems/ImageTextMatchFlip';
import ChoiceMatrixMulti from '../components/lessonItems/ChoiceMatrixMulti';
import Cloze from '../components/lessonItems/Cloze';
import ClozeCombo from '../components/lessonItems/ClozeCombo';
import ClozeDragDrop from '../components/lessonItems/ClozeDragDrop';
import ClozeMath from '../components/lessonItems/ClozeMath';
import ClozeMultichoice from '../components/lessonItems/ClozeMultichoice';
import ClozeSpelling from '../components/lessonItems/ClozeSpelling';

import AlchemieTestItem from '../components/lessonItems/AlchemieTestItem';
import StandaloneAlchemieItem from '../components/lessonItems/StandaloneAlchemieItem';
import AppletQuestion from '../components/lessonItems/AppletQuestion';
import Draw from '../components/lessonItems/Draw';
import DrawOverlay from '../components/lessonItems/DrawOverlay';
import DependentMultiChoice from '../components/lessonItems/DependentMultiChoice';
import LongText from '../components/lessonItems/LongText';
import Multichoice from '../components/lessonItems/Multichoice';
import Multiselect from '../components/lessonItems/Multiselect';
import NumberLine from '../components/lessonItems/NumberLine';
import OrderList from '../components/lessonItems/OrderList';
import OrderSentences from '../components/lessonItems/OrderSentences';
import ShortText from '../components/lessonItems/ShortText';
import Griddable from '../components/lessonItems/Griddable';
import StudentInstruction from '../components/lessonItems/StudentInstruction';
import TeacherInstruction from '../components/lessonItems/TeacherInstruction';
import Presentation from '../components/lessonItems/Presentation';
import TextSelector from '../components/lessonItems/TextSelector';
import HotText from '../components/lessonItems/HotText';
import TextHighlight from '../components/lessonItems/TextHighlight';
import Keyboarding from '../components/lessonItems/Keyboarding';
import Upload from '../components/lessonItems/Upload';
import ImageLabel from '../components/lessonItems/ImageLabel';
import ImageLabelDragDrop from '../components/lessonItems/ImageLabelDragDrop';
import ImageHotSpot from '../components/lessonItems/ImageHotSpot';
import Categorize from '../components/lessonItems/Categorize';
import DiInfo from '../components/diInfoPage/DiInfoPage';
import WebPage from '../components/lessonItems/WebPage';

import BubbleWebGraphicOrganizer from '../components/lessonItems/BubbleWebGraphicOrganizer';
import CauseAndEffect from '../components/lessonItems/CauseAndEffect';
import KWLChart from '../components/lessonItems/KWLChart';
import Relationships from '../components/lessonItems/Relationships';
import TChart from '../components/lessonItems/TChart';
import Venn from '../components/lessonItems/Venn';
import WordAssociation from '../components/lessonItems/WordAssociation';

// Activities
import BookmarkActivity from '../components/lessonItems/BookmarkActivity';
import PdfActivity from '../components/lessonItems/PdfActivity';
import BookActivity from '../components/lessonItems/BookActivity';
import MicrositeQuestion from '../components/lessonItems/MicrositeQuestion';

import ImageQuestion from '../components/lessonItems/ImageQuestion';
import AudioQuestion from '../components/lessonItems/AudioQuestion';
import VideoQuestion from '../components/lessonItems/VideoQuestion';
import AlchemieQuestion from '../components/lessonItems/AlchemieQuestion';

import ArticleQuestion from '../components/lessonItems/ArticleQuestion';
import MultipartActivity from '../components/lessonItems/MultipartActivity';
import PdfResource from '../components/lessonItems/PdfResource';
import Rubric from '../components/lessonItems/RubricResourceViewer';
import ImageResource from '../components/lessonItems/ImageResource';
import VideoResource from '../components/lessonItems/VideoResource';
import AudioResource from '../components/lessonItems/AudioResource';
import ChoiceMatrix from '../components/lessonItems/ChoiceMatrix';

// Non-mutating helper: walk `parts` into `obj` and return a new root with the
// value set at that path. Each modified level is shallow-cloned (spread /
// array copy). When `createMissing` is true, missing plain-object parents are
// created (used for validation.* LM overlays); array indexes are never invented.
const setAtPath = (obj, parts, value, depth = 0, createMissing = false) => {
  if (!obj || typeof obj !== 'object') return obj;
  const key = parts[depth];
  const isLast = depth === parts.length - 1;
  const isArr = Array.isArray(obj);
  const idx = parseInt(key, 10);

  if (isLast) {
    if (isArr) {
      if (isNaN(idx) || idx < 0 || idx >= obj.length) return obj;
      const copy = [...obj];
      copy[idx] = value;
      return copy;
    }
    return { ...obj, [key]: value };
  }

  let child = isArr ? obj[idx] : obj[key];
  if (child == null || typeof child !== 'object') {
    // Only create plain-object parents (e.g. validation.feedback) — never invent array slots.
    if (!createMissing || isArr) return obj;
    child = {};
  }

  const updated = setAtPath(child, parts, value, depth + 1, createMissing);
  if (updated === child && (isArr ? obj[idx] : obj[key]) === child) return obj;

  if (isArr) {
    if (isNaN(idx) || idx < 0 || idx >= obj.length) return obj;
    const copy = [...obj];
    copy[idx] = updated;
    return copy;
  }
  return { ...obj, [key]: updated };
};

const hasNonEmptyLmValue = (value) => value != null && String(value).trim() !== '';

// Language models may be stored as flat dotted keys or as nested objects
// (AI translate). Flatten so overlay lookups can use one key shape.
const flattenLanguageModelSubset = (node, prefix = '', out = {}) => {
  if (node == null) return out;
  if (typeof node !== 'object') {
    if (prefix && prefix !== 'id' && !/\.id$/.test(prefix)) {
      out[prefix] = node;
    }
    return out;
  }
  if (Array.isArray(node)) {
    node.forEach((item, index) => {
      flattenLanguageModelSubset(item, prefix ? `${prefix}.${index}` : String(index), out);
    });
    return out;
  }
  Object.keys(node).forEach((key) => {
    if (key === 'id') return;
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    if (nextPrefix === 'id' || /\.id$/.test(nextPrefix)) return;
    flattenLanguageModelSubset(node[key], nextPrefix, out);
  });
  return out;
};

export class LessonManager {
  @observable currentSlide = -1;
  @observable isDirectionsFlyoutOpen = false;
  @observable isInteractiveFlyoutOpen = false;
  @observable isInteractiveViewer = false;
  @observable isQuestionListView = false;
  @observable lesson;
  @observable lessonContentItem;
  @observable lessonOptions;
  @observable lessonResourceUrl = '';
  @observable loaded = 0;
  @observable childLoaded = 0;
  /** True while exit / save-for-later is persisting responses before leaving the player */
  @observable isExitSaving = false;
  @observable manualSlideList = [];
  @observable doNotScoreSlideList = [];
  @observable onlyShowManual = false;
  @observable onlyShowDoNotScore = false;
  @observable playerMode = null;
  @observable publisherTheme = null;
  @observable rootObject;
  @observable sectionList = new Map();
  @observable slideList = [];
  @observable startWithOnlyManual = false;
  @observable themeLoaded = false;
  @observable translationsLoaded = false;
  @observable isLti = false;
  @observable currentAudioId = null;
  @observable isSat = false;
  @observable isOld = false;
  @observable isForceTeacher = false;
  @observable slideLabels = [];
  @observable ignoreQuestionChildEventScrollListener = false;
  @observable isManuscriptShort = false;
  @observable isOnline = true;
  @observable docReaderUrl = null;
  @observable showSatelliteButton = false;
  @observable isSatelliteOpen = false;
  @observable showAssignmentsSatelliteButton = false;
  @observable shouldShowRelatedTabButton = false;

  @observable lessonMainLessonElementId = '';

  @observable translationLanguagesCache = null;
  @observable publisherTranslationLocaleId = 'en';
  

  LETTER_TABLE= ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'] ;

  interactiveElementId = null;

  lessonElementIdRequested = null;
  onlyShowManualOff = false;

  structureMap;
  contentMap;
  @observable activeC2CTranslationId = null;
  @observable c2cContentRevision = 0;
  dependencies;
  firstSection;
  clozeIds = [];
  activityPartIds = new Set();
  courseContentItemId = null;
  contentNoteText = null;

  resourceTypes = new Set([
    ContentType.IMAGE.type,
    ContentType.ARTICLE.type,
    ContentType.AUDIO.type,
    ContentType.VIDEO.type,
    ContentType.BOOKMARK.type,
    ContentType.PDF.type,
    ContentType.RUBRIC.type,
    ContentType.USER_FILE.type,
    ContentType.KEYBOARDING.type,
  ]);

  activityTypes = new Set([
    ContentType.ALCHEMIE_QUESTION.type,
    ContentType.ARTICLE_QUESTION.type,
    ContentType.MULTIPART_ACTIVITY.type,
    ContentType.VIDEO_QUESTION.type,
    ContentType.AUDIO_QUESTION.type,
    ContentType.IMAGE_QUESTION.type,
    ContentType.BOOKMARK_ACTIVITY.type,
    ContentType.BOOK_ACTIVITY.type,
    ContentType.PDF_ACTIVITY.type,
  ]);

  interactiveActivityTypes = new Set([
    ContentType.AUDIO_QUESTION.type,
    ContentType.VIDEO_QUESTION.type,
    ContentType.BOOK_ACTIVITY.type,
    ContentType.IMAGE_QUESTION.type,
  ]);

  multiAnswerTypes = new Set([
    ContentType.IMAGE_HOT_SPOT.type,
    ContentType.MULTISELECT.type,
    ContentType.HOT_TEXT.type,
    ContentType.IMAGE_TEXT_MATCH,
    ContentType.TEXT_SELECTOR
  ]);

  gameTypes = new Set([
    ContentType.IMAGE_TEXT_MATCH_FLIP.type
  ]);

  thirdPartyApiScoring = new Set([
    ContentType.ALCHEMIE_TEST_ITEM.type,
    ContentType.STAND_ALONE_ALCHEMIE_ITEM.type
  ]);

  hasDirectionFlyout = new Set([
    ContentType.BOOK_ACTIVITY.type,
    ContentType.AUDIO_QUESTION.type,
    ContentType.VIDEO_QUESTION.type,
    ContentType.BOOKMARK_ACTIVITY.type,
  ])

  constructor() {
    makeObservable(this);
  }

  @action
  setRootObject = (obj) => {
    this.rootObject = obj;
  }

  @action
  setStartWithOnlyManual = () => {
    if (this.lessonOptions.showOnlyManuallyGradedQuestions) {
      this.setOnlyShowManual(true);
    }

    if (this.lessonOptions.showUngradedSurveyQuestions) {
      this.setOnlyShowDoNotScore(true);
    }
  }

  @action
  createSlideLabels = () => {
    this.slideLabels = [];
    this.slideList.forEach((lessonElementId) => {
      const model = this.getSlideModel(lessonElementId);
      if (model && !model.isActivityPart) {
        this.slideLabels.push(lessonElementId);
      }
    });
  }

  @action
  setLessonMainLessonElementId = (lessonMainLessonElementId) => {
    this.lessonMainLessonElementId = lessonMainLessonElementId;
  }

  @action
  setOnlyShowManual = (toggle) => {
    if (toggle && this.manualSlideList.length === 0) {
      this.onlyShowManual = false;
      return false;
    }

    this.onlyShowManual = toggle;
    this.setCurrentSlideIndex(0);
    return true;
  }

  setManuscriptShort = (toggle) => {
    this.isManuscriptShort = toggle;
  }

  setOnlyShowDoNotScore = (toggle) => {
    if (toggle && this.doNotScoreSlideList.length === 0) {
      this.onlyShowDoNotScore = false;
      return false;
    }
    this.onlyShowDoNotScore = toggle;

    this.setCurrentSlideIndex(0);
    return true;
  }

  @action
  setContentItem = (ci) => {
    ci.entityType = ci.entityTypeId.toUpperCase();
    this.lessonContentItem = ci;
  }

  @action
  setLoaded = (loaded) => {
    if (loaded === 0) {
      this.loaded = 0;
      return;
    }
    this.loaded += loaded;
  }

  @action
  setIsExitSaving = (isExitSaving) => {
    this.isExitSaving = !!isExitSaving;
  }

  @action
  setChildLoaded = (childLoaded) => {
    if (childLoaded === 0) {
      this.childLoaded = 0;
      return;
    }
    this.childLoaded += childLoaded;
  }

  @action
  setModelValidation = (lessonElementId, validation) => {
    // Write to the contentMap source model — not getSlideModel(), which returns an
    // ephemeral C2C-translated copy when a translation is active.
    const model = this.getSourceSlideModel(lessonElementId);
    if (!model || !validation) return;

    model.validation = UtilsService.safeMobxClone(validation);

    const translationId = this.activeC2CTranslationId;
    if (!translationId) return;

    // Remount lesson chrome so SolutionDialog / feedback re-read the overlaid model.
    this.bumpC2CContentRevision();

    // If the active language model is missing validation.solution but we now have
    // source solution text (common for older on-the-fly translates), backfill via ECMS.
    const lm = model.languagemodels?.[translationId];
    const lmSolution = lm?.['validation.solution'];
    if (lm && hasNonEmptyLmValue(validation.solution) && !hasNonEmptyLmValue(lmSolution)) {
      // Lazy import avoids a LessonManager ↔ LessonService cycle.
      import('../services/LessonService').then(({ default: lessonService }) => {
        lessonService.ensureC2CTranslation(translationId, lessonElementId)
          .then(() => this.bumpC2CContentRevision())
          .catch(() => {});
      });
    }
  }

  /** Raw contentMap model (no C2C overlay). */
  getSourceSlideModel = (lessonElementId) => {
    const lessonElement = this.structureMap?.[lessonElementId];
    if (!lessonElement) return null;
    return this.contentMap?.[lessonElement.entityId] || null;
  }

  @computed get isLoaded() {
    return (this.loaded <= 0);
  }

  @computed get isChildLoaded() {
    return (this.childLoaded <= 0);
  }

  @action
  setLessonOptions = (lessonOptions) => {
    this.lessonOptions = lessonOptions;
  }

  /**
   * Translation id to attach to student response payloads.
   * Uses the active C2C Translate selection when set; otherwise 'default'.
   */
  getResponsePublisherTranslationId = () => {
    return this.activeC2CTranslationId || 'default';
  }

  /**
   * Stamp the language active at the moment the student types / changes an answer.
   * Scoring compares typed text to languagemodels[this id].validation answers.
   */
  stampResponsePublisherTranslationId = (responseAnswer) => {
    if (responseAnswer && typeof responseAnswer === 'object') {
      responseAnswer.publisherTranslationId = this.getResponsePublisherTranslationId();
    }
    return responseAnswer;
  }

  /**
   * Prefer the language stamped when the student answered. Fall back to the
   * current lesson language only when the response has not been stamped yet.
   */
  resolveResponsePublisherTranslationId = (responseAnswer) => {
    const stored = responseAnswer?.publisherTranslationId;
    if (typeof stored === 'string' && stored.trim() !== '') {
      return stored;
    }
    return this.getResponsePublisherTranslationId();
  }

  @action
  applyC2CTranslation = (translationId) => {
    this.activeC2CTranslationId = translationId || null;
    // MultichoiceManager is the only singleton that caches answer display order
    // across renders (used by Multichoice, Multiselect, DependentMultiChoice).
    // All other question types either read directly from the model each render
    // or use React useState, which reinitializes automatically on remount.
    // Clearing here forces those three types to rebuild from the freshly
    // translated model when the lesson-window remounts.
    multichoiceManager.clearMultichoiceItems();
    this.patchCachedTranslationDependentResponses();
  }

  @action
  bumpC2CContentRevision = () => {
    // Same cache invalidation as applyC2CTranslation: when a language model
    // arrives after Multichoice/Multiselect already rendered English answers,
    // remount alone is not enough — the singleton still holds the old texts.
    multichoiceManager.clearMultichoiceItems();
    this.patchCachedTranslationDependentResponses();
    this.c2cContentRevision += 1;
  }

  /**
   * Update response-state fields that were initialized once from the model
   * (e.g. LongText defaultText) and would otherwise keep the original language
   * after a C2C translation change.
   */
  patchCachedTranslationDependentResponses = () => {
    responseManager.lessonElementStates.forEach((lessonElementState, lessonElementId) => {
      const model = this.getSlideModel(lessonElementId);
      if (!model) return;

      const isOrderType = model.type === ContentType.ORDER_LIST.type ||
        model.type === ContentType.ORDER_SENTENCES.type;
      const isCategorize = model.type === ContentType.CATEGORIZE.type;
      const isLongText = model.type === ContentType.LONG_TEXT.type;
      const shouldRefreshCorrectAnswers = lessonElementState.correctAnswerShowing && (
        model.type === ContentType.IMAGE_LABEL.type ||
        model.type === ContentType.IMAGE_LABEL_DRAG_DROP.type ||
        model.type === ContentType.IMAGE_LABEL_MULTICHOICE.type ||
        model.type === ContentType.CLOZE.type ||
        model.type === ContentType.CLOZE_COMBO.type
      );

      if (shouldRefreshCorrectAnswers) {
        lessonElementState.questionBehavior?.setCorrectAnswer?.(lessonElementState, model);
      }

      if (!isCategorize && !isOrderType && !isLongText) return;

      const response = lessonElementState.currentResponseAnswer;
      if (!response) return;

      // Use the translated model when a translation is active, or the original
      // model when reverting to default — both cases need cached text patched.
      const sourceModel = this.applyC2CTranslationToModel(model) || model;

      if (isLongText) {
        this.patchLongTextDefaultTextResponse(lessonElementId, sourceModel, response);
        return;
      }

      const translatedTextById = new Map();
      if (isOrderType) {
        sourceModel.prompts?.forEach((item) => {
          if (item?.id && item.text) translatedTextById.set(item.id, item.text);
        });
      } else {
        const vPrompts = sourceModel.validation?.prompts;
        if (vPrompts && !Array.isArray(vPrompts) && typeof vPrompts === 'object') {
          Object.values(vPrompts).forEach((cell) => {
            if (Array.isArray(cell)) {
              cell.forEach((item) => {
                if (item?.id && item.text) translatedTextById.set(item.id, item.text);
              });
            }
          });
        }
      }

      if (translatedTextById.size === 0) return;

      if (isOrderType) {
        response.promptObjects?.forEach((item) => {
          const translated = item?.id && translatedTextById.get(item.id);
          if (translated) item.text = translated;
        });
        return;
      }

      const patchZone = (zone) => {
        if (!zone || typeof zone !== 'object') return;
        Object.values(zone).forEach((arr) => {
          if (!Array.isArray(arr)) return;
          arr.forEach((item) => {
            const translated = item?.id && translatedTextById.get(item.id);
            if (translated) item.text = translated;
          });
        });
      };

      patchZone(response.bankPrompts);
      patchZone(response.cellPrompts);
    });
  }

  normalizeTranslationHtmlText = (html) => UtilsService.stripTagsAndEntities(html || '').trim();

  collectLongTextDefaultPlaceholders = (rawModel) => {
    const placeholders = new Set();
    const add = (value) => {
      const normalized = this.normalizeTranslationHtmlText(value);
      if (normalized) placeholders.add(normalized);
    };
    add(rawModel?.defaultText);
    const languageModels = rawModel?.languagemodels;
    if (languageModels && typeof languageModels === 'object') {
      Object.keys(languageModels).forEach((key) => {
        const subset = languageModels[key];
        if (subset && typeof subset === 'object') {
          add(subset.defaultText);
        }
      });
    }
    return placeholders;
  }

  patchLongTextDefaultTextResponse = (lessonElementId, translatedModel, response) => {
    const entityId = this.structureMap?.[lessonElementId]?.entityId;
    const rawModel = entityId ? this.contentMap?.[entityId] : null;
    if (!rawModel || !response) return;

    const currentNorm = this.normalizeTranslationHtmlText(response.text);
    const placeholders = this.collectLongTextDefaultPlaceholders(rawModel);
    // Only replace when the student has not entered real content yet.
    if (currentNorm !== '' && !placeholders.has(currentNorm)) return;

    response.text = translatedModel?.defaultText || '';
  }

  applyC2CTranslationToModel = (model) => {
    const translationId = this.activeC2CTranslationId;
    if (!translationId || !model?.languagemodels?.[translationId]) return model;
    const defaultModel = flattenLanguageModelSubset(model.languagemodels.default || {});
    const translationModel = flattenLanguageModelSubset(model.languagemodels[translationId] || {});
    const overrides = {};
    Object.keys(translationModel).forEach((key) => {
      const val = translationModel[key];
      overrides[key] = (val !== '' && val != null) ? val : defaultModel[key];
    });

    // Rebuild an answers array using translated text from overrides.
    // Language models store answer translations as flat keys: answer_<id>
    const translateAnswers = (answers) => {
      if (!answers?.length) return answers;
      return answers.map((answer) => {
        const translatedText = overrides[`answer_${answer.id}`];
        return translatedText ? { ...answer, text: translatedText } : answer;
      });
    };

    // OrderList / OrderSentences: language models store prompt text as prompt_<id>
    // (and legacy prompts.N.text). Visible model.prompts may be shuffled relative to
    // the language-model order, so match by prompt id instead of extending/replacing
    // by array index.
    const isOrderListType = model.type === ContentType.ORDER_LIST.type ||
      model.type === ContentType.ORDER_SENTENCES.type;
    const translateOrderPromptsById = (prompts) => {
      if (!prompts?.length) return prompts;
      const idToText = {};

      prompts.forEach((prompt) => {
        if (!prompt?.id) return;
        const byId = overrides[`prompt_${prompt.id}`];
        if (byId) idToText[prompt.id] = byId;
      });

      // Legacy / alignment: map positional prompts.N.text through the LM's remembered
      // id order (or validation order when that metadata is absent).
      let previousOrder = null;
      const orderRaw = overrides.orderListPromptOrder;
      if (typeof orderRaw === 'string' && orderRaw) {
        try {
          const parsed = JSON.parse(orderRaw);
          if (Array.isArray(parsed) && parsed.length) previousOrder = parsed;
        } catch (e) {
          previousOrder = null;
        }
      }
      if (!previousOrder?.length && model.validation?.prompts?.length) {
        previousOrder = model.validation.prompts;
      }

      if (previousOrder?.length) {
        previousOrder.forEach((id, index) => {
          if (!id || idToText[id] !== undefined) return;
          const byPosition = overrides[`prompts.${index}.text`];
          if (byPosition) idToText[id] = byPosition;
        });
      }

      return prompts.map((prompt) => {
        const translated = prompt?.id && idToText[prompt.id];
        return translated ? { ...prompt, text: translated } : prompt;
      });
    };

    // Multichoice, Multiselect — top-level answers array
    const answers = translateAnswers(model.answers) ?? model.answers;

    // OrderList / OrderSentences — top-level prompts array matched by id
    const isImageLabelFamily = model.type === ContentType.IMAGE_LABEL.type
      || model.type === ContentType.IMAGE_LABEL_DRAG_DROP.type
      || model.type === ContentType.IMAGE_LABEL_MULTICHOICE.type;
    let prompts = isOrderListType
      ? (translateOrderPromptsById(model.prompts) ?? model.prompts)
      : model.prompts;

    // ClozeMultichoice, ImageLabelMultichoice — answers nested in validation.prompts
    // ClozeDragDrop / Cloze — answers use positional keys: prompt_N_answer_M_text
    // Image Label family (typing, drag-drop, multichoice) — publisher writes
    // dotted validation.prompts.N.answers.M.text keys
    // Skip OrderList / OrderSentences: validation.prompts is an array of prompt ids
    // (strings), not prompt objects with nested answers.
    let validation = model.validation;
    const validationPrompts = model.validation?.prompts;
    if (
      Array.isArray(validationPrompts) &&
      validationPrompts.length &&
      typeof validationPrompts[0] === 'object' &&
      validationPrompts[0] !== null
    ) {
      validation = {
        ...model.validation,
        prompts: validationPrompts.map((prompt, promptIdx) => {
          const answers = prompt.answers?.map((answer, answerIdx) => {
            // Image Label family (current publisher) uses dotted validation paths.
            // Prefer those over leftover answer_${id} / prompt_N_answer_M_text keys,
            // which older snapshots may still have in English.
            const byDotted = overrides[`validation.prompts.${promptIdx}.answers.${answerIdx}.text`];
            // ClozeMultichoice / ImageLabelMultichoice use answer_${id}
            const byId = answer && overrides[`answer_${answer.id}`];
            // ClozeDragDrop / Cloze use positional prompt_N_answer_M_text keys
            const byPosition = overrides[`prompt_${promptIdx}_answer_${answerIdx}_text`];
            const translated = byDotted || byId || byPosition;
            if (!translated) return answer;
            return (answer && typeof answer === 'object')
              ? { ...answer, text: translated }
              : { text: translated };
          }) ?? prompt.answers;

          let correctAnswers = prompt.correctAnswers;
          if (Array.isArray(prompt.correctAnswers)) {
            correctAnswers = prompt.correctAnswers.map((value, answerIdx) => {
              const byDotted = overrides[`validation.prompts.${promptIdx}.correctAnswers.${answerIdx}`];
              if (byDotted) return byDotted;
              if (typeof value !== 'string' || !answers?.length) return value;
              const originalIdx = prompt.answers?.findIndex((answer) => (
                (typeof answer === 'string' ? answer : answer?.text) === value
              ));
              const translatedAnswer = originalIdx >= 0 ? answers[originalIdx] : answers[answerIdx];
              const translatedText = typeof translatedAnswer === 'string'
                ? translatedAnswer
                : translatedAnswer?.text;
              return translatedText || value;
            });
          }

          return { ...prompt, answers, correctAnswers };
        })
      };
    }

    // ClozeDragDrop — distractors use validation.distractors.N.text keys
    if (model.validation?.distractors?.length) {
      const translatedDistractors = model.validation.distractors.map((distractor, idx) => {
        const translated = overrides[`validation.distractors.${idx}.text`];
        return translated ? { ...distractor, text: translated } : distractor;
      });
      validation = { ...validation, distractors: translatedDistractors };
    }

    // Legacy / alternate LM shape: nested `validation: { solution, feedback }` object
    // instead of flat `validation.solution` keys. Only merge when source validation
    // already exists — same rule as the dotted-key pass below.
    const nestedLmValidation = overrides.validation;
    if (
      validation &&
      nestedLmValidation &&
      typeof nestedLmValidation === 'object' &&
      !Array.isArray(nestedLmValidation)
    ) {
      const {
        prompts: _lmPrompts,
        distractors: _lmDistractors,
        ...nestedTextFields
      } = nestedLmValidation;
      void _lmPrompts;
      void _lmDistractors;
      validation = {
        ...validation,
        ...nestedTextFields,
        ...(validation.prompts !== undefined ? { prompts: validation.prompts } : {}),
        ...(validation.distractors !== undefined ? { distractors: validation.distractors } : {}),
      };
    }

    // responseItems is the flat answer bank rendered to the student (correct answers
    // + distractors combined). Rebuild it from the now-translated validation data so
    // the answer bank tiles show translated text.
    let responseItems = model.responseItems;
    if (model.responseItems?.length && (validation?.prompts?.length || validation?.distractors?.length)) {
      const translatedTextById = new Map();
      const altTextById = new Map();
      validation.prompts?.forEach((prompt) => {
        prompt.answers?.forEach((answer) => {
          translatedTextById.set(answer.id, answer.text);
          if (typeof answer.altText !== 'undefined') {
            altTextById.set(answer.id, answer.altText || '');
          }
        });
      });
      validation.distractors?.forEach((distractor) => {
        translatedTextById.set(distractor.id, distractor.text);
        if (typeof distractor.altText !== 'undefined') {
          altTextById.set(distractor.id, distractor.altText || '');
        }
      });
      responseItems = model.responseItems.map((item) => {
        const translated = translatedTextById.get(item.id);
        const next = translated ? { ...item, text: translated } : { ...item };
        if (altTextById.has(item.id)) {
          next.altText = altTextById.get(item.id);
        }
        if (model.responseFormat === 'image' && typeof next.altText !== 'undefined') {
          next.text = UtilsService.getImageResponseItemHtml(next);
        }
        return next;
      });
    }

    // DependentMultiChoice — answers nested in questions array
    let questions = model.questions;
    if (model.questions?.length) {
      questions = model.questions.map((question) => ({
        ...question,
        answers: translateAnswers(question.answers) ?? question.answers
      }));
    }

    // Nested identity-bearing objects in languagemodels must not replace live
    // answers/targets/prompts. AI translate has been seen to copy translated
    // labels into both `id` and `text` on those objects.
    const nestedIdentityKeys = new Set([
      'answers', 'prompts', 'targets', 'validation', 'responseItems', 'questions', 'languagemodels'
    ]);
    const scalarOverrides = {};
    Object.keys(overrides).forEach((key) => {
      if (nestedIdentityKeys.has(key)) return;
      const value = overrides[key];
      if (value != null && typeof value === 'object') return;
      scalarOverrides[key] = value;
    });

    // Base result: flat spread covers simple top-level keys (questionText, body,
    // slideSummary, etc.) and explicit rebuilds cover answers / validation /
    // questions / responseItems. For OrderList / OrderSentences, explicit prompts
    // (matched by id) wins over any language-model prompts payload so visible order is kept.
    let result = (isOrderListType || isImageLabelFamily)
      ? { ...model, ...scalarOverrides, answers, prompts, validation, questions, responseItems }
      : { ...model, ...scalarOverrides, answers, validation, questions, responseItems };

    // Second pass: apply any dot-notation keys (e.g. rowLabels.0,
    // columnLabels.1, validation.prompts.cell0.0.text, prompts.0.text,
    // validation.solution) that need to update nested model structure rather than
    // a top-level property.
    // OrderList / OrderSentences already applied prompts by id — skip positional
    // prompts.N.text so a reshuffled LM order cannot overwrite the wrong item.
    // validation.* overlays require an existing validation object (usually injected
    // after submit). Do not invent top-level validation from the LM alone — that
    // would make getSlideModel() appear to already have validation and skip
    // setModelValidation of server scoring data. Intermediate plain objects under
    // an existing validation (e.g. validation.feedback) may still be created.
    for (const [key, value] of Object.entries(overrides)) {
      if (!key.includes('.') || value == null || value === '') continue;
      if (key === 'id' || key.endsWith('.id')) continue;
      if (isOrderListType && /^prompts\.\d+\.text$/.test(key)) continue;
      const parts = key.split('.');
      if (result[parts[0]] == null) continue;
      const canCreateValidationPath = parts[0] === 'validation';
      result = setAtPath(result, parts, value, 0, canCreateValidationPath);
    }

    // ImageTextMatch uses target.text as the droppable/scoring identity. Keep the
    // original-language value as target.id so translated labels stay display-only.
    if (Array.isArray(model.targets) && Array.isArray(result.targets)) {
      result = {
        ...result,
        targets: result.targets.map((target, index) => {
          const original = model.targets[index];
          const stableId = original?.id || original?.text;
          return stableId ? { ...target, id: stableId } : target;
        })
      };
    }

    // Categorize: validation.prompts is an object keyed by cellN (not an array).
    // After the dot-path pass updates cell item texts, sync the translated text
    // back into model.prompts and model.responseItems, which are separate copies
    // of the same items used to render the draggable answer-bank chips.
    if (
      result.validation?.prompts &&
      !Array.isArray(result.validation.prompts) &&
      typeof result.validation.prompts === 'object'
    ) {
      const translatedTextById = new Map();
      Object.values(result.validation.prompts).forEach((cell) => {
        if (Array.isArray(cell)) {
          cell.forEach((item) => {
            if (item?.id && item.text) translatedTextById.set(item.id, item.text);
          });
        }
      });

      if (translatedTextById.size > 0) {
        if (result.prompts && !Array.isArray(result.prompts) && typeof result.prompts === 'object') {
          const updatedPrompts = {};
          for (const [cellKey, cell] of Object.entries(result.prompts)) {
            updatedPrompts[cellKey] = Array.isArray(cell)
              ? cell.map((item) => {
                const translated = translatedTextById.get(item.id);
                return translated ? { ...item, text: translated } : item;
              })
              : cell;
          }
          result = { ...result, prompts: updatedPrompts };
        }

        if (result.responseItems?.length) {
          result = {
            ...result,
            responseItems: result.responseItems.map((item) => {
              const translated = translatedTextById.get(item.id);
              return translated ? { ...item, text: translated } : item;
            })
          };
        }
      }
    }

    result = this.stripHtmlFromTranslatedTypingAnswers(result);
    return this.syncImageLabelTranslatedCopies(result);
  }

  /**
   * Publisher translation editors wrap typed accepted answers in <p> tags.
   * Strip that markup for Cloze / ClozeCombo typing and Image Label family so
   * scoring display, dropdown labels, and Show Correct Answer use the same
   * plain translated text. Image Label copies stripped answer texts onto
   * correctAnswers, which otherwise stays in the original language.
   */
  stripHtmlFromTranslatedTypingAnswers = (model) => {
    if (!Array.isArray(model?.validation?.prompts)) {
      return model;
    }

    const isClozeTyping = model.type === ContentType.CLOZE.type || model.type === ContentType.CLOZE_COMBO.type;
    const isImageLabelTyping = model.type === ContentType.IMAGE_LABEL.type
      && model.responseFormat === 'text';
    const isImageLabelFamily = model.type === ContentType.IMAGE_LABEL.type
      || model.type === ContentType.IMAGE_LABEL_DRAG_DROP.type
      || model.type === ContentType.IMAGE_LABEL_MULTICHOICE.type;
    if (!isClozeTyping && !isImageLabelFamily) {
      return model;
    }

    const prompts = model.validation.prompts.map((prompt) => {
      const shouldStrip = isImageLabelFamily || UtilsService.isTypingBlankPrompt(model, prompt);
      if (!shouldStrip) {
        return prompt;
      }

      const answers = prompt.answers?.map((answer) => (
        typeof answer?.text === 'string'
          ? { ...answer, text: UtilsService.getPlainTextAnswerDisplay(model, answer.text) }
          : answer
      ));

      let correctAnswers = Array.isArray(prompt.correctAnswers)
        ? prompt.correctAnswers.map((value) => (
          typeof value === 'string' ? UtilsService.getPlainTextAnswerDisplay(model, value) : value
        ))
        : prompt.correctAnswers;

      if (isImageLabelFamily && answers?.length) {
        const fromAnswers = answers
          .filter((answer) => {
            if (!answer?.text) return false;
            if (isImageLabelTyping || model.type === ContentType.IMAGE_LABEL.type) return true;
            return typeof answer.isCorrect !== 'boolean' || answer.isCorrect;
          })
          .map((answer) => answer.text);
        if (fromAnswers.length) {
          correctAnswers = fromAnswers;
        }
      }

      return {
        ...prompt,
        ...(answers ? { answers } : {}),
        ...(correctAnswers !== undefined ? { correctAnswers } : {})
      };
    });

    let next = { ...model, validation: { ...model.validation, prompts } };
    if (isImageLabelFamily && Array.isArray(next.validation.distractors)) {
      next = {
        ...next,
        validation: {
          ...next.validation,
          distractors: next.validation.distractors.map((distractor) => (
            typeof distractor?.text === 'string'
              ? { ...distractor, text: UtilsService.getPlainTextAnswerDisplay(model, distractor.text) }
              : distractor
          ))
        }
      };
    }
    return next;
  }

  /**
   * Image Label typing / drag-drop / multichoice keep answer text in more than
   * one place. After validation.prompts.answers is translated, copy that text
   * onto the answer bank (responseItems) and per-prompt dropdown options
   * (prompts.responseItems).
   */
  syncImageLabelTranslatedCopies = (model) => {
    const isImageLabelFamily = model?.type === ContentType.IMAGE_LABEL.type
      || model?.type === ContentType.IMAGE_LABEL_DRAG_DROP.type
      || model?.type === ContentType.IMAGE_LABEL_MULTICHOICE.type;
    if (!isImageLabelFamily || !Array.isArray(model.validation?.prompts)) {
      return model;
    }

    const translatedTextById = new Map();
    const altTextById = new Map();
    model.validation.prompts.forEach((prompt) => {
      prompt.answers?.forEach((answer) => {
        if (!answer?.id) return;
        if (answer.text) translatedTextById.set(answer.id, answer.text);
        if (typeof answer.altText !== 'undefined') {
          altTextById.set(answer.id, answer.altText || '');
        }
      });
    });
    model.validation.distractors?.forEach((distractor) => {
      if (!distractor?.id) return;
      if (distractor.text) translatedTextById.set(distractor.id, distractor.text);
      if (typeof distractor.altText !== 'undefined') {
        altTextById.set(distractor.id, distractor.altText || '');
      }
    });

    if (!translatedTextById.size && !altTextById.size) {
      return model;
    }

    let next = model;
    if (Array.isArray(next.prompts)) {
      next = {
        ...next,
        prompts: next.prompts.map((prompt) => {
          if (!prompt.responseItems?.length) return prompt;
          return {
            ...prompt,
            responseItems: prompt.responseItems.map((item) => {
              const translated = translatedTextById.get(item.id);
              return translated ? { ...item, text: translated } : item;
            })
          };
        })
      };
    }

    if (next.responseItems?.length) {
      next = {
        ...next,
        responseItems: next.responseItems.map((item) => {
          const translated = translatedTextById.get(item.id);
          const nextItem = translated ? { ...item, text: translated } : { ...item };
          if (altTextById.has(item.id)) {
            nextItem.altText = altTextById.get(item.id);
          }
          if (next.responseFormat === 'image' && typeof nextItem.altText !== 'undefined') {
            nextItem.text = UtilsService.getImageResponseItemHtml(nextItem);
          }
          return nextItem;
        })
      };
    }

    return next;
  }

  @action
  setLesson = (lesson) => {
    this.lesson = lesson;
  }

  @action
  setPublisherTheme = (publisherTheme) => {
    this.publisherTheme = publisherTheme;
  }

  @action
  setThemeLoaded = (loaded) => {
    this.themeLoaded = loaded;
  }

  @action
  setTranslationsLoaded = (loaded) => {
    this.translationsLoaded = loaded;
  }

  @computed
  get hasNoTheme() {
    return this.themeLoaded && this.publisherTheme === null;
  }

  @action
  setIsLti = (val) => {
    this.isLti = val;
  }

  @action
  setIsSat = (val) => {
    this.isSat = val;
  }

  @action
  setIsOld = (val) => {
    this.isOld = val;
  }

  @action
  setIsForceTeacher = (val) => {
    this.isForceTeacher = val;
  }

  @action
  setIsQuestionListView = (toggle) => {
    this.isQuestionListView = toggle;
  }

  @action setIgnoreQuestionChildEventScrollListener = (toggle) => {
    this.ignoreQuestionChildEventScrollListener = toggle;
  }

  @action
  setPlayerMode = (mode) => {
    this.playerMode = mode;
  }

  @action
  setResourceUrl = (url) => {
    this.lessonResourceUrl = url;
  }

  @action
  setCurrentAudioId = (id) => {
    this.currentAudioId = id;
  }

  @action
  setIsInteractiveViewer = (val) => {
    this.isInteractiveViewer = val;
  }

  @action
  setInteractiveElementId = (id) => {
    this.interactiveElementId = id;
  }

  @action
  setIsInteractiveFlyoutOpen = (isOpen) => {
    this.isInteractiveFlyoutOpen = isOpen;
  }

  @action
  setIsDirectionsFlyoutOpen = (isOpen) => {
    this.isDirectionsFlyoutOpen = isOpen;
  }

  @action
  setShowSatelliteButton = (show) => {
    this.showSatelliteButton = show;
  }

  @action
  setIsSatelliteOpen = (isOpen) => {
    this.isSatelliteOpen = isOpen;
  }

  @action
  setShowAssignmentsSatelliteButton = (show) => {
    this.showAssignmentsSatelliteButton = show;
  }

  @action
  setShouldShowRelatedTabButton = (show) => {
    this.shouldShowRelatedTabButton = show;
  }

  @action
  setOnline = (isOnline) => {
    this.isOnline = isOnline;
  }

  @action
  setDocReaderUrl = (url) => {
    this.docReaderUrl = url;
  }

  @action setContentNoteText = (text) => {
    this.contentNoteText = text;
  }

  @action setTranslationLanguagesCache = (translationLanguages) => {
    this.translationLanguagesCache = translationLanguages;
  }

  @action clearTranslationLanguagesCache = () => {
    this.translationLanguagesCache = null;
  }

  @action setPublisherTranslationLocaleId = (localeId) => {
    this.publisherTranslationLocaleId = localeId;
  }

  @computed get currentLessonElementId() {
    const slides = this.getFilteredSlides();

    if (slides.length > 0 && this.currentSlide < slides.length) {
      return slides[this.currentSlide];
    }
    if (slides.length > 0) {
      return slides[0];
    }

    return null;
  }

  @computed get lessonElementIds() {
    const slides = this.getFilteredSlides();

    return slides;
  }

  @computed get currentParentLessonElementId() {
    const slides = this.getFilteredSlides();

    if (slides.length > 0 && this.currentSlide < slides.length) {
      const lessonElementId = slides[this.currentSlide];
      const currentModel = this.getSlideModel(lessonElementId);
      if (!UtilsService.isNullOrEmpty(currentModel.parentActivityElementId)) {
        return currentModel.parentActivityElementId;
      }
    }
    if (slides.length > 0) {
      return slides[0];
    }
    return null;
  }

  @computed get currentRespondableLessonElementId() {
    const lessonElementId = this.currentLessonElementId;
    const model = this.getSlideModel(lessonElementId);
    if (this.shouldHaveElementState(model)) {
      return lessonElementId;
    }
    return null;
  }

  shouldHaveElementState = (model) => {
    if (model.isTestItem || model.isStudentInstruction || model.isActivity || model.isResource || model.isInstruction) {
      return true;
    }
    return false;
  }

  @computed get lessonTitle() {
    let finalTitle = (this.lessonContentItem) ? this.lessonContentItem.name : '';
    if (this.lessonOptions && this.lessonOptions.title) {
      finalTitle = this.lessonOptions.title;
    }

    return finalTitle;
  }

  @computed get lessonTitleAndSubtitle() {
    let finalTitle = this.lessonTitle;
    if (this.lessonOptions && this.lessonOptions.showSubtitleHeader && this.lessonOptions.subtitle) {
      finalTitle = `${this.lessonOptions.title.trim()}: ${this.lessonOptions.subtitle}`;
    }
    return finalTitle;
  }

  @computed get lessonGotoTitle() {
    let finalTitle = null;
    if (this.lessonOptions && this.lessonOptions.showTitleGoto && this.lessonOptions.title) {
      finalTitle = `${this.lessonOptions.title.trim()}`;
      if (this.lessonOptions.showSubtitleGoto && this.lessonOptions.subtitle) {
        finalTitle = `${finalTitle}: ${this.lessonOptions.subtitle}`;
      }
    }
    return finalTitle;
  }

  /**
   * Single entry for changing the visible slide. Summary cards, goto cards, activity nav, footer
   * next/previous, and programmatic jumps all resolve to an index here.
   *
   * PDF activity: the slide index always ends on a **child** item (`currentLessonElementId`). If the
   * index pointed at the PDF **activity container**, we advance to the first filtered child (same as
   * today). For that child we read `childPages` and drive docReader to that page. `entryViaActivityParent`
   * is only true for that parent→first-child jump so flyout/page-1 UX can distinguish it from jumping
   * straight to a child (any navigation). A deferred `goToPage` catches the case where this runs
   * before the PdfActivity iframe has a content window.
   *
   * @param {object} [options]
   * @param {boolean} [options.pdfGotoPageOne] Goto menu entry to a PDF activity: open docReader at page 1
   *   (slide is still a concrete child — see NavMenuService).
   */
  @action
  setCurrentSlideIndex = (index, fromLaunch = false, options = {}) => {
    const { pdfGotoPageOne = false } = options;
    let modelElement = this.getModelAndLessonElement(index);
    if (!modelElement.model || !modelElement.lessonElement) {
      this.currentSlide = 0;
      return;
    }

    const navigatedViaActivityParent = modelElement.model.isActivity &&
      modelElement.lessonElement.lessonElementIds.length > 0;
    const activityParentWasPdfActivity = navigatedViaActivityParent &&
      modelElement.model.type === ContentType.PDF_ACTIVITY.type;

    if (navigatedViaActivityParent) {
      this.currentSlide = index + 1; // first child
      modelElement = this.getModelAndLessonElement(this.currentSlide); // get the first child for previous assignment support
    } else {
      this.currentSlide = index;
    }

    docReaderManager.clearLessonDirectedPdfNavigation();
    if (modelElement.model.isActivityPart) {
      const parentModel = this.getSlideModel(modelElement.model.parentActivityElementId);
      if (parentModel?.type === ContentType.PDF_ACTIVITY.type) {
        const targetPage = pdfGotoPageOne
          ? 1
          : getPageForActivityChild(parentModel, modelElement.model);
        if (targetPage != null) {
          const pdfChildLessonElementId = modelElement.model.lessonElementId;
          docReaderManager.setLessonDirectedPdfNavigation({
            targetPage,
            entryViaActivityParent: activityParentWasPdfActivity || pdfGotoPageOne,
            pdfParentLessonElementId: parentModel.lessonElementId,
          });
          docReaderManager.applyLessonDirectedPdfNavigation();
          this.scheduleAlignDocReaderToCurrentPdfChild(pdfChildLessonElementId);
        }
      }
    }

    if(modelElement.model.lessonElementId) {

      let lessonElementIds = modelElement.lessonElement.id;

      if (modelElement.model.isActivity && modelElement.lessonElement.lessonElementIds && modelElement.lessonElement.lessonElementIds[0]) {
        const firstchildElementId = modelElement.lessonElement.lessonElementIds[0];
        lessonElementIds = `${lessonElementIds},${firstchildElementId}`;
      }
      if (modelElement.model.isActivityPart) {
        const parentLessonElementId = modelElement.model.parentActivityElementId;
        lessonElementIds = `${parentLessonElementId},${lessonElementIds}`;
      }

      lessonElementIds = `${this.lessonMainLessonElementId},${lessonElementIds}`;

      if (modelElement.lessonElement && !UtilsService.isNullOrEmpty(lessonElementIds)) {
        this.setShowSatelliteButton(true);
        window.parent.postMessage({ type: 'current_elements', lessonElementIds }, toolbarManager.externalUrl);
      } else {
        this.setShowSatelliteButton(false);
        if (!fromLaunch) { window.parent.postMessage({ type: 'current_elements', lessonElementIds: '' }, toolbarManager.externalUrl); }
      }
    } else {
       this.setShowSatelliteButton(false);
       window.parent.postMessage({ type: 'current_elements', lessonElementIds: '' }, toolbarManager.externalUrl);
    }

    const previousAssignments = (modelElement && modelElement.model.accessPreviousAssignments && (this.playerMode === LessonMode.ACTIVITY_MODE || this.playerMode === LessonMode.TEACHER_PRESENTATION));
    this.setShowAssignmentsSatelliteButton(!!previousAssignments);
    window.parent.postMessage({ type: 'access_assignments', previousAssignments }, toolbarManager.externalUrl);
  }

  /**
   * Page for `scheduleAlign` / `alignDocReaderToCurrentPdfChildAfterIframeReady`: honor lesson-directed
   * `pdfLessonPendingReaderPage` first; for goto→page-1 do not let a later align jump from page 1 to
   * the slide child’s `childPages` index while the goto landing flag is still set.
   */
  _getPdfAlignTargetPageForCurrentChild = (parentModel, childModel) => {
    if (parentModel?.type !== ContentType.PDF_ACTIVITY.type || !childModel) {
      return null;
    }
    const pending = docReaderManager.pdfLessonPendingReaderPage;
    if (pending != null && !Number.isNaN(Number(pending))) {
      return Number(pending);
    }
    const childPage = getPageForActivityChild(parentModel, childModel);
    const cur = Number(docReaderManager.currentPageNumber);
    if (
      docReaderManager.pdfGotoOutsideActivityNavPageOne &&
      cur === 1 &&
      (childPage == null || Number(childPage) !== 1)
    ) {
      return 1;
    }
    return childPage;
  }

  /**
   * Re-reads the current slide’s PDF child page from `childPages` and calls `goToPage` once the iframe
   * exists. Invoked from `setCurrentSlideIndex` and from docReader `pageloaded` (first document load only).
   */
  scheduleAlignDocReaderToCurrentPdfChild = (expectedChildLessonElementId) => {
    const align = () => {
      if (this.currentLessonElementId !== expectedChildLessonElementId) {
        return;
      }
      const childModel = this.getSlideModel(this.currentLessonElementId);
      if (!childModel?.isActivityPart) {
        return;
      }
      const parentModel = this.getSlideModel(childModel.parentActivityElementId);
      if (parentModel?.type !== ContentType.PDF_ACTIVITY.type) {
        return;
      }
      const targetPage = this._getPdfAlignTargetPageForCurrentChild(parentModel, childModel);
      if (targetPage == null) {
        return;
      }
      docReaderManager.goToPage(Number(targetPage));
    };

    setTimeout(align, 50);
  }

  /**
   * Called from PdfActivity after the iframe is wired — final alignment when navigation began
   * before `contentWindow` existed (summary, back button, goto).
   */
  alignDocReaderToCurrentPdfChildAfterIframeReady = () => {
    const childId = this.currentLessonElementId;
    if (!childId) {
      return;
    }
    const childModel = this.getSlideModel(childId);
    if (!childModel?.isActivityPart) {
      return;
    }
    const parentModel = this.getSlideModel(childModel.parentActivityElementId);
    if (parentModel?.type !== ContentType.PDF_ACTIVITY.type) {
      return;
    }
    const targetPage = this._getPdfAlignTargetPageForCurrentChild(parentModel, childModel);
    if (targetPage == null) {
      return;
    }
    if (!docReaderManager.isLivePdfActivityIframe(parentModel.lessonElementId)) {
      return;
    }
    docReaderManager.goToPage(Number(targetPage));
  }

  getParentLastSlide = (parentLessonElement) => {
    const slides = this.getFilteredSlides();
    const filteredChildren = [];
    for (const childId of parentLessonElement.lessonElementIds) {
      if (slides.indexOf(childId) > -1) {
        filteredChildren.push(childId);
      }
    }
    if (filteredChildren.length <= 0) {
      return null;
    }
    return filteredChildren[filteredChildren.length - 1];
  }

  getParentFirstSlide = (parentLessonElement) => {
    const slides = this.getFilteredSlides();
    const filteredChildren = [];
    for (const childId of parentLessonElement.lessonElementIds) {
      if (slides.indexOf(childId) > -1) {
        filteredChildren.push(childId);
      }
    }
    if (filteredChildren.length <= 0) {
      return null;
    }
    return filteredChildren[0];
  }

  /**
   * Same resolution as `setCurrentSlideIndex`: landing on a PDF activity container advances to the first child.
   */
  involvesPdfActivitySlideAtIndex = (index) => {
    if (index == null || index < 0) {
      return false;
    }
    let modelElement = this.getModelAndLessonElement(index);
    if (!modelElement?.model || !modelElement.lessonElement) {
      return false;
    }
    const navigatedViaActivityParent = modelElement.model.isActivity &&
      modelElement.lessonElement.lessonElementIds?.length > 0;
    if (navigatedViaActivityParent && modelElement.model.type === ContentType.PDF_ACTIVITY.type) {
      modelElement = this.getModelAndLessonElement(index + 1);
    }
    if (!modelElement?.model) {
      return false;
    }
    const { model } = modelElement;
    if (model.type === ContentType.PDF_ACTIVITY.type) {
      return true;
    }
    const parentModel = model.parentActivityElementId
      ? this.getSlideModel(model.parentActivityElementId)
      : null;
    return parentModel?.type === ContentType.PDF_ACTIVITY.type;
  }

  /**
   * Mirrors `nextSlide()` slide index math without mutating. Null when `nextSlide` would not advance.
   */
  getTargetSlideIndexAfterNextSlide = () => {
    const slides = this.getFilteredSlides();
    if (this.currentSlide === (slides.length - 1)) {
      if (this.playerMode === LessonMode.SCORING) {
        return 0;
      }
      return null;
    }
    if (this.lessonOptions.navigateWithinActivity) {
      return this.currentSlide + 1;
    }
    const modelElement = this.getModelAndLessonElement(this.currentSlide);
    let newIndex = this.currentSlide + 1;
    if (modelElement.model?.isActivityPart) {
      const parentLessonElement = this.getLessonElement(modelElement.model.parentActivityElementId);
      const lastChildId = this.getParentLastSlide(parentLessonElement);
      if (lastChildId) {
        newIndex = this.getIndexOfLessonElementId(lastChildId);
        if (newIndex === (slides.length - 1)) {
          if (this.playerMode === LessonMode.SCORING) {
            return 0;
          }
          return null;
        }
        return newIndex + 1;
      }
    }
    return newIndex;
  }

  @action
  nextSlide = () => {
    try {
      if (window.ReadSpeaker && window.ReadSpeaker.ui?.getActivePlayer?.()) {
        window.ReadSpeaker.ui.getActivePlayer().close();
        window.ReadSpeaker.ui.destroyActivePlayer();
      }
      const slides = this.getFilteredSlides();
      if (this.currentSlide === (slides.length - 1)) {
        if (this.playerMode === LessonMode.SCORING) {
          this.setCurrentSlideIndex(0);
        }
        return;
      }
      if (this.lessonOptions.navigateWithinActivity) {
        this.setCurrentSlideIndex(this.currentSlide + 1);
      } else {
        const modelElement = this.getModelAndLessonElement(this.currentSlide);
        let newIndex = this.currentSlide + 1;
        if (modelElement.model.isActivityPart) {
          const parentLessonElement = this.getLessonElement(modelElement.model.parentActivityElementId);
          const lastChildId = this.getParentLastSlide(parentLessonElement);
          if (lastChildId) {
            newIndex = this.getIndexOfLessonElementId(lastChildId);
            if (newIndex === (slides.length - 1)) {
              if (this.playerMode === LessonMode.SCORING) {
                this.setCurrentSlideIndex(0);
              }
              return;
            }
            this.setCurrentSlideIndex(newIndex + 1);
            return;
          }
        }
        this.setCurrentSlideIndex(newIndex);
      }
    } catch (error) {
      console.error(error);
    }
  }

  checkNextSlide = () => {
    try {
      const slides = this.slideList;
      if (this.currentSlide === (slides.length - 1)) {
        return null;
      }
      return slides[this.currentSlide + 1];
    } catch (error) {
      console.error(error);
    }
  }

  getFilteredSlides = () => {
    if (this.onlyShowManual && !this.onlyShowDoNotScore) {
      const list = [].concat(this.manualSlideList);
      if (this.playerMode === LessonMode.SCORING && this.lessonOptions.hasSummaryPage) {
        list.push(LessonSlideIds.SUMMARY_PAGE);
      }
      return list;
    }

    if (!this.onlyShowManual && this.onlyShowDoNotScore) {
      const list = [].concat(this.doNotScoreSlideList);
      if (this.playerMode === LessonMode.SCORING && this.lessonOptions.hasSummaryPage) {
        list.push(LessonSlideIds.SUMMARY_PAGE);
      }
      return list;
    }

    if (this.onlyShowManual && this.onlyShowDoNotScore) {
      const list = [];
      this.slideList.forEach((slide, index) => {
        if (this.manualSlideList.includes(slide) || this.doNotScoreSlideList.includes(slide)) {
          list.push(slide);
        }
      });
      if (this.playerMode === LessonMode.SCORING && this.lessonOptions.hasSummaryPage) {
        list.push(LessonSlideIds.SUMMARY_PAGE);
      }
      return list;
    }
    return this.slideList;
  }

  @computed get numberOfQuestions() {
    let count = 0;
    for (let i = 0; i < this.slideList.length; i++) {
      const model = this.getSlideModel(this.slideList[i]);
      if (model && model.isTestItem) {
        ++count;
      }
    }
    return count;
  }

  @action
  previousSlide = () => {
    try {
      if (window.ReadSpeaker && window.ReadSpeaker.ui?.getActivePlayer?.()) {
        window.ReadSpeaker.ui.getActivePlayer().close();
        window.ReadSpeaker.ui.destroyActivePlayer();
      }
      const slides = this.getFilteredSlides();
      if (this.currentSlide === 0) {
        if (this.playerMode === LessonMode.SCORING) {
          this.setCurrentSlideIndex(slides.length - 1);
        }
        return;
      }

      if (this.lessonOptions.navigateWithinActivity) {
        const modelElement = this.getModelAndLessonElement(this.currentSlide);
        if (this.isActivityFirstChild(modelElement)) {
          if (this.currentSlide - 2 < 0) {
            return;
          } else {
            this.setCurrentSlideIndex(this.currentSlide - 2);
            return;
          }
        }
        this.setCurrentSlideIndex(this.currentSlide - 1);
      } else {
        const modelElement = this.getModelAndLessonElement(this.currentSlide);
        if (modelElement.model.isActivityPart) {
          if (this.isActivityFirstChild(modelElement)) {
            if (this.currentSlide - 2 < 0) {
              return;
            } else {
              const modelElement = this.getModelAndLessonElement(this.currentSlide - 2);
              if (modelElement.model.isActivityPart) {
                const parentLessonElement = this.getLessonElement(modelElement.model.parentActivityElementId);
                const firstChildId = this.getParentFirstSlide(parentLessonElement);
                if (firstChildId) {
                  const newIndex = this.getIndexOfLessonElementId(firstChildId);
                  this.setCurrentSlideIndex(newIndex);
                  return;
                }
              } else {
                this.setCurrentSlideIndex(this.currentSlide - 2);
                return;
              }
            }
          } else {
            const parentLessonElement = this.getLessonElement(modelElement.model.parentActivityElementId);
            const firstChildId = parentLessonElement.lessonElementIds[0];
            if (firstChildId) {
              const newIndex = this.getIndexOfLessonElementId(firstChildId);
              if (newIndex - 2 < 0) {
                return;
              } else {
                const checkElement = this.getModelAndLessonElement(newIndex - 2);
                if (checkElement.model.isActivityPart) {
                  const parentLessonElement = this.getLessonElement(checkElement.model.parentActivityElementId);
                  const firstChildId = parentLessonElement.lessonElementIds[0];
                  if (firstChildId) {
                    const newIndex = this.getIndexOfLessonElementId(firstChildId);
                    this.setCurrentSlideIndex(newIndex);
                    return;
                  }
                } else {
                  this.setCurrentSlideIndex(newIndex - 2);
                }
                return;
              }
            }
          }
        }
        const checkElement = this.getModelAndLessonElement(this.currentSlide - 1);
        if (checkElement.model.isActivityPart) {
          const parentLessonElement = this.getLessonElement(checkElement.model.parentActivityElementId);
          const firstChildId = parentLessonElement.lessonElementIds[0];
          if (firstChildId) {
            const newIndex = this.getIndexOfLessonElementId(firstChildId);
            this.setCurrentSlideIndex(newIndex);
          }
        } else {
          this.setCurrentSlideIndex(this.currentSlide - 1);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  // this should only be called in Activity Mode
  checkPreviousSlide = () => {
    try {
      const modelElement = this.getModelAndLessonElement(this.currentSlide);
      if (this.isActivityFirstChild(modelElement)) {
        if (this.currentSlide - 2 < 0) {
          return null;
        } else {
          return this.slideList[this.currentSlide - 2];
        }
      }
      return this.slideList[this.currentSlide - 1];
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  @action
  addSlide = (lessonElementId) => {
    this.slideList.push(lessonElementId);
  }

  @action
  addManuallyScoredSlide = (lessonElementId) => {
    this.manualSlideList.push(lessonElementId);
  }

  addDoNotScoreSlide = (lessonElementId) => {
    this.doNotScoreSlideList.push(lessonElementId);
  }

  @computed get showNavBackButton() {
    // eslint-disable-next-line no-unused-vars
    const slides = this.getFilteredSlides();
    const mode = this.playerMode;

    if (mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION || mode === LessonMode.PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
      const previousLessonElementId = this.checkPreviousSlide();
      const lessonElementState = responseManager.getLessonElementState(previousLessonElementId);
      if (lessonElementState && lessonElementState.isDependencyLocked) {
        return true;
      }
    }

    if (this.currentSlide <= 0) {
      return false;
    }

    const modelElement = this.getModelAndLessonElement(this.currentSlide);

    if (this.currentSlide === 1 && this.isActivityFirstChild(modelElement)) {
      return false;
    }
    return true;
  }

  @computed get isAssessment() {
    return this.lesson?.contentItem?.entityTypeId === 'assessment';
  }

  @computed get isLesson() {
    return this.lesson?.contentItem?.entityTypeId === 'lesson';
  }

  @computed get showNavNextButton() {
    const slides = this.getFilteredSlides();
    const mode = this.playerMode;
    if (mode === LessonMode.ACTIVITY_MODE || mode === LessonMode.TEACHER_PRESENTATION || mode === LessonMode.PREVIEW ||  mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
      let nextLessonElementId = this.checkNextSlide();
      const nextModel = this.getSlideModel(nextLessonElementId);
      const nextElement = this.getLessonElement(nextLessonElementId);
      if (nextModel && nextModel.isActivity && nextElement.lessonElementIds && nextElement.lessonElementIds.length > 0) {
        nextLessonElementId = nextElement.lessonElementIds[0];
      }
      const lessonElementState = responseManager.getLessonElementState(nextLessonElementId);
      if (lessonElementState && lessonElementState.isDependencyLocked) {
        return false;
      }
    }

    if (this.currentSlide >= slides.length - 1) {
      return false;
    }
    return true;
  }

  @computed get sectionLessonElementIds() {
    const keyIt = this.sectionList.keys();
    if (keyIt) {
      return Array.from(keyIt);
    }
    return null;
  }

  @computed
  get elementsThatHaveState() {
    const returnList = [];
    // iterating using the slideList to ensure the models
    // are sorted in slide order.
    for (let i = 0; i < this.slideList.length; ++i) {
      const lessonElement = this.structureMap[this.slideList[i]];
      const model = this.contentMap[lessonElement.entityId];
      if (this.shouldHaveElementState(model)) {
        returnList.push(model);
      }
    }
    return returnList;
  }

  @computed
  get isReadOnly() {
    if (this.playerMode === LessonMode.REVIEW ||
      this.playerMode === LessonMode.STUDENT_PREVIEW ||
      this.playerMode === LessonMode.PRINT_PREVIEW ||
      this.playerMode === LessonMode.SCORING ||
      this.playerMode === LessonMode.MANUSCRIPT) {
      return true;
    }
    return false;
  }

  @computed
  get slideNumberLabel() {
    const slides = this.slideLabels;

    const model = this.getSlideModel(this.currentLessonElementId);
    const currentIndex = slides.indexOf(this.currentLessonElementId) + 1;

    if (model.isActivityPart) {
      const parentLessonElement = this.structureMap[model.parentActivityElementId];
      const parentIndex = slides.indexOf(parentLessonElement.id) + 1;
      const childIndex = parentLessonElement.lessonElementIds.indexOf(model.lessonElementId) + 1;
      return `${parentIndex}.${childIndex}/${slides.length}`;
    }
    if (model.isActivity) {
      return `${currentIndex}.0/${slides.length}`;
    }
    return `${currentIndex}/${slides.length}`;
  }

 @computed
  get currentAriaMessage() {
    if (this.isLoaded) {
      const ariaLiveMessage = `You are now viewing slide number ${this.currentSlideNumber}`;
      return ariaLiveMessage;
    } else {
      return '';
    }
  }

 get currentSlideNumber() {
   const slides = this.slideLabels;

   const model = this.getSlideModel(this.currentLessonElementId);
   if (!model) {
     return '0';
   }
   const currentIndex = slides.indexOf(this.currentLessonElementId) + 1;

   if (model.isActivityPart) {
     const parentLessonElement = this.structureMap[model.parentActivityElementId];
     const arrayIndex = slides.indexOf(parentLessonElement.id);
     const parentIndex = arrayIndex + 1;
     const childIndex = parentLessonElement.lessonElementIds.indexOf(model.lessonElementId) + 1;
     return `${parentIndex}.${childIndex}`;
   }
   if (model.isActivity) {
     return `${currentIndex}.0`;
   }
   return `${currentIndex}`;
 }

  @computed
 get questionNumberLabel() {
   const model = this.getSlideModel(this.currentLessonElementId);
   if (model.questionNumber === null || model.questionNumber === undefined) {
     return null;
   }
   const number = model.questionNumber;

   if (UtilsService.isNullOrEmpty(number)) {
     return '';
   }

   return `${number}/${this.numberOfQuestions}`;
 }

  get currentItemNumber() {
    const model = this.getSlideModel(this.currentLessonElementId);
    if (model.questionNumber === null || model.questionNumber === undefined) {
      return '-1';
    }
    return model.questionNumber;
  }

  getIndexOfLessonElementId = (lessonElementId) => {
    const slides = this.getFilteredSlides();
    return slides.indexOf(lessonElementId);
  }

  @computed
  get currentSectionTitle() {
    const model = this.getSlideModel(this.currentLessonElementId);
    if (!model || !model.sectionLessonElementId) {
      return null;
    }
    const sectionModel = this.getSectionModel(model.sectionLessonElementId);
    if (!sectionModel) {
      return null;
    }
    const { title } = sectionModel;
    // TODO remove per CF-3048
    // if (Utils.isNullOrEmpty(title)) {
    //   title = `Section ${sectionModel.sectionNumber}`;
    // }
    return title;
  }

  // TODO remove
  // @computed
  // get isCurrentModeUsingTranslations() {
  //   return this.playerMode === LessonMode.STUDENT_PREVIEW ||
  //     this.playerMode === LessonMode.PREVIEW ||
  //     this.playerMode === LessonMode.ITEM_PREVIEW ||
  //     this.playerMode === LessonMode.REVIEW ||
  //     this.playerMode === LessonMode.SCORING ||
  //     this.playerMode === LessonMode.TEACHER_PRESENTATION ||
  //     this.playerMode === LessonMode.ACTIVITY_MODE;
  // }

  getActivityComponent(lessonElementId) {
    const model = this.getSlideModel(lessonElementId);

    let Activity;
    if (model && model.isActivity) {
      switch (model.type) {
        case ContentType.MULTIPART_ACTIVITY.type:
          Activity = MultipartActivity;
          break;
        case ContentType.VIDEO_QUESTION.type:
          Activity = VideoQuestion;
          break;
        case ContentType.AUDIO_QUESTION.type:
          Activity = AudioQuestion;
          break;
        case ContentType.ALCHEMIE_QUESTION.type:
          Activity = AlchemieQuestion;
          break;
        case ContentType.ARTICLE_QUESTION.type:
          Activity = ArticleQuestion;
          break;
        case ContentType.IMAGE_QUESTION.type:
          Activity = ImageQuestion;
          break;
        case ContentType.BOOKMARK_ACTIVITY.type:
          Activity = BookmarkActivity;
          break;
        case ContentType.BOOK_ACTIVITY.type:
          Activity = BookActivity;
          break;
        case ContentType.PDF_ACTIVITY.type:
          Activity = PdfActivity;
          break;
        default:
          Activity = DefaultActivity;
      }
    }
    if (model) {
      return Activity;
    }
    return null;
  }

  getCurrentQuestionComponent(lessonElementId) {
    const model = this.getSlideModel(lessonElementId);
    let Question;
    if (model && model.isQuestion) {
      switch (model.type) {
        case ContentType.MULTICHOICE.type:
          Question = Multichoice;
          break;
        case ContentType.DEPENDENT_MULTI_CHOICE.type:
          Question = DependentMultiChoice;
          break;
        case ContentType.MULTISELECT.type:
          Question = Multiselect;
          break;
        case ContentType.ALCHEMIE_TEST_ITEM.type:
          Question = AlchemieTestItem;
          break;
        case ContentType.STAND_ALONE_ALCHEMIE_ITEM.type:
          Question = StandaloneAlchemieItem;
          break;
        case ContentType.APPLET_QUESTION.type:
          Question = AppletQuestion;
          break;
        case ContentType.SHORT_TEXT.type:
          Question = ShortText;
          break;
        case ContentType.LONG_TEXT.type:
          Question = LongText;
          break;
        case ContentType.UPLOAD.type:
          Question = Upload;
          break;
        case ContentType.CLOZE.type:
          Question = Cloze;
          break;
        case ContentType.CLOZE_COMBO.type:
          Question = ClozeCombo;
          break;
        case ContentType.CLOZE_DRAG_DROP.type:
          Question = ClozeDragDrop;
          break;
        case ContentType.CLOZE_MULTICHOICE.type:
          Question = ClozeMultichoice;
          break;
        case ContentType.CLOZE_MATH.type:
          Question = ClozeMath;
          break;
        case ContentType.CATEGORIZE.type:
          Question = Categorize;
          break;
        case ContentType.IMAGE_HOT_SPOT.type:
          Question = ImageHotSpot;
          break;
        case ContentType.IMAGE_LABEL.type:
          Question = ImageLabel;
          break;
        case ContentType.IMAGE_LABEL_MULTICHOICE.type:
          Question = ImageLabelMultichoice;
          break;
        case ContentType.IMAGE_LABEL_DRAG_DROP.type:
          Question = ImageLabelDragDrop;
          break;
        case ContentType.DRAW.type:
          Question = Draw;
          break;
        case ContentType.DRAW_OVERLAY.type:
          Question = DrawOverlay;
          break;
        case ContentType.CHOICE_MATRIX.type:
          Question = ChoiceMatrix;
          break;
        case ContentType.CHOICE_MATRIX_MULTI.type:
          Question = ChoiceMatrixMulti;
          break;
        case ContentType.TEXT_SELECTOR.type:
          Question = TextSelector;
          break;
        case ContentType.TEXT_HIGHLIGHT.type:
          Question = TextHighlight;
          break;
        case ContentType.HOT_TEXT.type:
          Question = HotText;
          break;
        case ContentType.ORDER_SENTENCES.type:
          Question = OrderSentences;
          break;
        case ContentType.ORDER_LIST.type:
          Question = OrderList;
          break;
        case ContentType.CLOZE_SPELLING.type:
          Question = ClozeSpelling;
          break;
        case ContentType.IMAGE_TEXT_MATCH.type:
          Question = ImageTextMatch;
          break;
        case ContentType.IMAGE_TEXT_MATCH_FLIP.type:
          Question = ImageTextMatchFlip;
          break;
        case ContentType.LIKERT.type:
          Question = Likert;
          break;
        case ContentType.NUMBER_LINE.type:
          Question = NumberLine;
          break;
        case ContentType.GRIDDABLE.type:
          Question = Griddable;
          break;
        case ContentType.MICROSITE_QUESTION.type:
          Question = MicrositeQuestion;
          break;
        default:
          Question = DefaultQuestion;
      }
    }

    let GraphicOrganizer;
    if (model && model.isGraphicOrganizer) {
      switch (model.type) {
        case ContentType.ANALOGIES.type:
          GraphicOrganizer = Analogies;
          break;
        case ContentType.BUBBLE_WEB.type:
          GraphicOrganizer = BubbleWebGraphicOrganizer;
          break;
        case ContentType.CATEGORY_TREE.type:
          GraphicOrganizer = CategoryTree;
          break;
        case ContentType.CAUSE_AND_EFFECT.type:
          GraphicOrganizer = CauseAndEffect;
          break;
        case ContentType.KWL_CHART.type:
          GraphicOrganizer = KWLChart;
          break;
        case ContentType.RELATIONSHIPS.type:
          GraphicOrganizer = Relationships;
          break;
        case ContentType.T_CHART.type:
          GraphicOrganizer = TChart;
          break;
        case ContentType.VENN.type:
          GraphicOrganizer = Venn;
          break;
        case ContentType.WORD_ASSOCIATION.type:
          GraphicOrganizer = WordAssociation;
          break;
        default:
          GraphicOrganizer = DefaultGraphicOrganizer;
      }
    }

    let Instruction;
    if (model && model.isInstruction) {
      switch (model.type) {
        case ContentType.PRESENTATION.type:
          Instruction = Presentation;
          break;
        case ContentType.STUDENT_INSTRUCTION.type:
          Instruction = StudentInstruction;
          break;
        case ContentType.TEACHER_INSTRUCTION.type:
          Instruction = TeacherInstruction;
          break;
        case ContentType.WEB_PAGE.type:
          Instruction = WebPage;
          break;
        default:
          Instruction = DefaultInstruction;
      }
    }

    let Resource;
    if (model && model.isResource) {
      switch (model.type) {
        case ContentType.IMAGE.type:
          Resource = ImageResource;
          break;
        case ContentType.VIDEO.type:
          Resource = VideoResource;
          break;
        case ContentType.AUDIO.type:
          Resource = AudioResource;
          break;
        case ContentType.PDF.type:
          Resource = PdfResource;
          break;
        case ContentType.RUBRIC.type:
          Resource = Rubric;
          break;
        case ContentType.KEYBOARDING.type:
          Resource = Keyboarding;
          break;
        default:
          Resource = DefaultResource;
      }
    }

    if (model) {
      if (model.isGraphicOrganizer) {
        return GraphicOrganizer;
      }
      if (model.isQuestion) {
        return Question;
      }
      if (model.isGoogle) {
        return Google;
      }
      if (model.isResource) {
        return Resource;
      }
      if (model.isInstruction) {
        return Instruction;
      }
      if (model.isDiInfo) {
        return DiInfo;
      }
      if (model.isTitlePage) {
        return TitlePage;
      }
      if (model.isSummaryPage) {
        return SummaryPage;
      }
    }

    return null;
  }

  isSelected = (lessonElementId) => {
    const currentId = this.currentLessonElementId;
    if (!currentId || !lessonElementId) {
      return false;
    }
    if (currentId === lessonElementId) {
      return true;
    }

    const model = this.getSlideModel(lessonElementId);
    const lessonElement = this.getLessonElement(lessonElementId);
    if (model?.isActivity && lessonElement?.lessonElementIds?.indexOf(currentId) > -1) {
      return true;
    }
    return false;
  }

  getSlideModelFromTotalOnly(lessonElementId) {
    const theSlides = this.slideList;

    if (theSlides.length === 0 ||
      typeof this.structureMap === 'undefined' ||
      typeof this.contentMap === 'undefined') {
      return null;
    }

    const lessonElement = this.structureMap[lessonElementId];

    if (!lessonElement) {
      return null;
    }

    const model = this.contentMap[lessonElement.entityId];

    if (!model) {
      return null;
    }
    return this.applyC2CTranslationToModel(model);
  }

  getSlideModel(lessonElementId) {
    const theSlides = this.getFilteredSlides();

    if (theSlides.length === 0 ||
      typeof this.structureMap === 'undefined' ||
      typeof this.contentMap === 'undefined') {
      return null;
    }

    const lessonElement = this.structureMap[lessonElementId];

    if (!lessonElement) {
      return null;
    }

    const model = this.contentMap[lessonElement.entityId];

    if (!model) {
      return null;
    }
    return this.applyC2CTranslationToModel(model);
  }

  getSectionModel = (lessonElementId) => {
    const model = this.sectionList.get(lessonElementId);
    if (model) {
      return model;
    }
    return null;
  }

  studentHidden = (model, isStudent) => {
    if (isStudent && model.type === ContentType.TEACHER_INSTRUCTION.type) {
      return true;
    }
    if (isStudent && model.hideFromStudents) {
      return true;
    }
    return false;
  }

  getSectionLessonElementIdList = (sectionLessonElementId, isStudent, expandActivities = true) => {
    const lessonElementIdList = [];

    const sectionLessonElement = this.getLessonElement(sectionLessonElementId);

    if (sectionLessonElement.lessonElementIds && sectionLessonElement.lessonElementIds.length > 0) {
      sectionLessonElement.lessonElementIds.forEach((lessonElementId) => {
        const lessonElement = this.structureMap[lessonElementId];
        const visibleSlides = this.getFilteredSlides();
        if (lessonElement) {
          const model = this.contentMap[lessonElement.entityId];
          if (model && (model.isTestItem || model.isInstruction || model.isResource)) {
            const visible = visibleSlides.indexOf(lessonElementId) > -1;
            if (!this.studentHidden(model, isStudent) && visible) {
              lessonElementIdList.push(lessonElementId);
            }
          } else if (model && model.isActivity) {
            if (this.activityPartIds.size > 0) {
              if (expandActivities) {
                this.activityPartIds.forEach((activityChildLessonElementId) => {
                  const childLessonElement = this.structureMap[activityChildLessonElementId];
                  const childModel = this.contentMap[childLessonElement.entityId];
                  const visible = visibleSlides.indexOf(activityChildLessonElementId) > -1;
                  if (visible && (childModel.isTestItem || childModel.isInstruction || childModel.isResource) && childModel.parentActivityElementId === lessonElementId) {
                    lessonElementIdList.push(activityChildLessonElementId);
                  }
                });
              } else {
                // Old GO TO behavior: list the activity parent (with its question
                // range) and not its children, as long as a child is visible.
                let anyChildVisible = false;
                this.activityPartIds.forEach((activityChildLessonElementId) => {
                  const childLessonElement = this.structureMap[activityChildLessonElementId];
                  const childModel = this.contentMap[childLessonElement.entityId];
                  const visible = visibleSlides.indexOf(activityChildLessonElementId) > -1;
                  if (visible && childModel && childModel.parentActivityElementId === lessonElementId) {
                    anyChildVisible = true;
                  }
                });
                if (anyChildVisible || visibleSlides.indexOf(lessonElementId) > -1) {
                  lessonElementIdList.push(lessonElementId);
                }
              }
            } else {
              const visible = visibleSlides.indexOf(lessonElementId) > -1;
              if (visible) {
                lessonElementIdList.push(lessonElementId);
              }
            }
          }
        }
      });
    }
    return lessonElementIdList;
  }

  getLessonElement = (lessonElementId) => {
    const lessonElement = this.structureMap[lessonElementId];
    if (!lessonElement) {
      return null;
    }
    return lessonElement;
  }

  getModelAndLessonElement = (index) => {
    const theSlides = this.getFilteredSlides();
    if (index < 0) {
      return { model: null, lessonElement: null };
    }
    const lessonElementId = theSlides[index];
    if (!lessonElementId) {
      return { model: null, lessonElement: null };
    }
    const lessonElement = this.structureMap[lessonElementId];
    if (!lessonElement) {
      return { model: null, lessonElement: null };
    }
    const model = this.contentMap[lessonElement.entityId];
    if (!model) {
      return { model: null, lessonElement: null };
    }
    return { model, lessonElement };
  }

  isActivityFirstChild = (modelElement) => {
    if (!modelElement) {
      return false;
    }
    const { model } = modelElement;
    const { lessonElement } = modelElement;

    if (!model || !lessonElement) {
      return false;
    }

    if (!model.isActivityPart) {
      return false;
    }

    const parentModelElement = this.getModelAndLessonElement(this.currentSlide - 1);

    if (parentModelElement && parentModelElement.model && parentModelElement.model.isActivity) {
      return true;
    }

    return false;
  }

  addLessonWideListeners = () => {
    if (this.lessonOptions && this.lessonOptions.hasPreventCopyPaste && !userManager.hasCopyPasteOverride) {
      window.addEventListener('copy', this.preventActionListener);
      window.addEventListener('contextmenu', this.preventActionListener);
      // window.addEventListener('paste', this.preventActionListener);
    }
  }

  preventActionListener = (e) => {
    e = e || window.event;
    e.preventDefault();
  };
}

export default new LessonManager();
